import axios from "axios";
import { format, subMonths } from "date-fns";
import { getTotalByMonth, getDataByStartAndEndDate } from "./lib/callChartAPI";
import normalizeFiltersForAPI from "@/lib/normalizeFilters";

const getMonthlyStatusData = async (currentDate, storeId) => {
  const response = await getTotalByMonth("tm_monthly_status", format(currentDate, "yMM"), storeId);
  if (!response) {
    return { isError: true };
  }
  return {
    "successfully-parsed": response.data[0] || 0,
    "unsupported-category": response.data[3] || 0,
    "missing-measurements": response.data[4] || 0,
    "no-measurements": response.data[5] || 0,
    "vs-optimization-in-progress": response.data[-1] || 0,
    total: Object.values(response.data).reduce((t, n) => t + n) || 0,
  };
};

const getLatestItemsData = async (currentDate, storeId) => {
  const response = await getTotalByMonth("tm_latest_items", format(currentDate, "yMM"), storeId);
  const splitBySize = (original, statusKey) => {
    let newArray = [];
    original.forEach((org) => {
      let orgObject = {
        ...org,
        statusKey,
      };
      delete orgObject.sizes;
      if (!org.sizes) {
        newArray.push(orgObject);
        return;
      }
      Object.keys(org.sizes).forEach((size) => {
        let newSizeObject = {
          sizeName: size,
          sizes: org.sizes[size],
        };
        newArray.push({
          ...orgObject,
          ...newSizeObject,
        });
      });
    });
    return newArray;
  };

  return {
    "successfully-parsed": splitBySize(response.data[0], "successfully-parsed") || [],
    "unsupported-category": splitBySize(response.data[3], "unsupported-category") || [],
    "missing-measurements": splitBySize(response.data[4], "missing-measurements") || [],
    "no-measurements": splitBySize(response.data[5], "no-measurements") || [],
    "vs-optimization-in-progress": splitBySize(response.data[-1], "vs-optimization-in-progress") || [],
  };
};

const getTMStatusData = async (currentDate, storeId) => {
  const prevMonthDate = subMonths(currentDate, 1);
  const responses = await Promise.all([
    getTotalByMonth("tm_status", format(currentDate, "yMM"), storeId),
    getTotalByMonth("tm_status", format(prevMonthDate, "yMM"), storeId),
  ]);

  if (!responses[0]) {
    return { isError: true };
  }

  let total = Object.values(responses[0].data).reduce((t, n) => t + n);
  let prevTotalVal = responses[1] ? Object.values(responses[1].data).reduce((t, n) => t + n) : 0;

  return {
    "successfully-parsed": responses[0].data[0] || 0,
    "unsupported-category": responses[0].data[3] || 0,
    "missing-measurements": responses[0].data[4] || 0,
    "no-measurements": responses[0].data[5] || 0,
    "vs-optimization-in-progress": responses[0].data[-1] || 0,
    "total-percentage": total !== 0 ? responses[0].data[0] / total : 0,
    prevTotal: prevTotalVal !== 0 ? responses[1].data[0] / prevTotalVal : 0,
    total,
  };
};

const instanceForAPI = axios.create({
  baseURL: process.env["VUE_APP_LAMBDA_API"],
  headers: {},
});

const getDownloadLink = async (currentDate, storeId) => {
  const response = await instanceForAPI.get(`/download_items/${storeId}/${format(currentDate, "yyyy/MM")}`);
  return response.data;
};

export const getItemCoverageData = async (chartName, currentDate, storeId) => {
  switch (chartName) {
    case "tm_monthly_status":
      return getMonthlyStatusData(currentDate, storeId);
    case "tm_latest_items":
      return getLatestItemsData(currentDate, storeId);
    case "tm_status":
      return getTMStatusData(currentDate, storeId);
    default:
      return getDownloadLink(currentDate, storeId);
  }
};

export const getCoverageData = async (payload) => {
  const { chartName, startDate, endDate, storeId, filters } = payload;
  const normalizedFilters = normalizeFiltersForAPI(filters);
  delete normalizedFilters["c"]; // remove item category filter for coverage API call
  const response = await getDataByStartAndEndDate(chartName, startDate, endDate, storeId, normalizedFilters);

  return {
    data: response.data || [],
    total: response.total || 0,
  };
};
